import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ComponentValues,
  CourseDetail,
  CourseStudentRow,
  GradeStatus,
  TeacherCourse,
  TeacherPortalData,
  addGradeComponent,
  changeTeacherPassword,
  deleteGradeComponent,
  fetchCourseDetail,
  fetchTeacherPortal,
  finalizeCourse,
  forceChangeTeacherPassword,
  getTeacherSession,
  logoutTeacher,
  saveComponentGrades,
  updateGradeComponent,
  uploadTeacherPhoto,
} from '../../config/teacherApi';
import { SessionExpiredError } from '../../config/portalClient';
import {
  BLOCK_COLORS,
  ChangePasswordForm,
  Empty,
  IconCalificaciones,
  IconContrasena,
  IconHorario,
  PortalHeader,
  PrintButton,
  WeekSchedule,
  scheduleSlots,
} from './portal/PortalShared';
import './EstudianteDashboard.css';
import './DocenteDashboard.css';

const BASE = '/portal-laboral/inicio';

type SectionId = 'cursos' | 'estudiantes' | 'notas' | 'horario' | 'contrasena';

const IconCursos = () => (
  <svg viewBox="0 0 64 64" aria-hidden="true">
    <rect x="8" y="14" width="30" height="38" rx="3" fill="#0ea5e9" />
    <rect x="14" y="10" width="30" height="38" rx="3" fill="#22c55e" />
    <rect x="20" y="6" width="34" height="40" rx="3" fill="#e2e8f0" />
    <rect x="26" y="14" width="22" height="3" rx="1.5" fill="#94a3b8" />
    <rect x="26" y="21" width="18" height="3" rx="1.5" fill="#94a3b8" />
    <rect x="26" y="28" width="20" height="3" rx="1.5" fill="#94a3b8" />
    <circle cx="46" cy="48" r="10" fill="#a855f7" />
    <path d="M41 48h10M46 43v10" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

const IconEstudiantes = () => (
  <svg viewBox="0 0 64 64" aria-hidden="true">
    <circle cx="22" cy="22" r="8" fill="#94a3b8" />
    <path d="M8 48c0-8 6-13 14-13s14 5 14 13z" fill="#94a3b8" />
    <circle cx="42" cy="20" r="9" fill="#0ea5e9" />
    <path d="M26 50c0-9 7-15 16-15s16 6 16 15z" fill="#0ea5e9" />
  </svg>
);

const SECTIONS: { id: SectionId; label: string; icon: React.FC; perCourse: boolean }[] = [
  { id: 'cursos', label: 'Mis cursos', icon: IconCursos, perCourse: false },
  { id: 'estudiantes', label: 'Mis estudiantes', icon: IconEstudiantes, perCourse: true },
  { id: 'notas', label: 'Registrar notas', icon: IconCalificaciones, perCourse: true },
  { id: 'horario', label: 'Ver horario', icon: IconHorario, perCourse: false },
  { id: 'contrasena', label: 'Cambiar contraseña', icon: IconContrasena, perCourse: false },
];

const STATUS_LABEL: Record<GradeStatus, string> = {
  PENDING: 'En curso',
  PASSED: 'Aprobada',
  FAILED: 'Reprobada',
};

const STATUS_CLASS: Record<GradeStatus, string> = {
  PENDING: 'en_curso',
  PASSED: 'aprobado',
  FAILED: 'reprobado',
};

function courseName(course: TeacherCourse) {
  return `${course.subject.name} · Grupo ${course.label}`;
}

function isCurrent(course: TeacherCourse) {
  return course.pending_count > 0 || course.status === 'OPEN';
}

function formatNumber(value: number | null | undefined) {
  if (value === null || value === undefined) return '—';
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, '');
}

interface SectionProps {
  data: TeacherPortalData;
  courseId: number | null;
  onSelectCourse: (id: number) => void;
  onCourseChanged: () => void;
  mustChangePassword: boolean;
  onPasswordChanged: () => void;
}

const CourseFilter: React.FC<{ courses: TeacherCourse[]; courseId: number | null; onSelect: (id: number) => void }> = ({
  courses, courseId, onSelect,
}) => (
  <label className="est-filter">
    <span>Curso:</span>
    <select value={courseId ?? ''} onChange={e => onSelect(Number(e.target.value))}>
      {courses.map(c => (
        <option key={c.id} value={c.id}>
          {courseName(c)} — {c.career.name} · {c.module.name}
        </option>
      ))}
    </select>
  </label>
);

function useCourseDetail(courseId: number | null) {
  const [detail, setDetail] = useState<CourseDetail | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!courseId) return;
    try {
      setDetail(await fetchCourseDetail(courseId));
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar el curso.');
    }
  }, [courseId]);

  useEffect(() => {
    setDetail(null);
    load();
  }, [load]);

  return { detail, setDetail, error };
}

const MisCursos: React.FC<SectionProps> = ({ data }) => {
  if (!data.courses.length) {
    return <Empty>Aún no tienes cursos asignados. Cuando la institución te asigne un grupo aparecerá aquí.</Empty>;
  }
  return (
    <table className="est-table est-table--framed doc-courses">
      <thead>
        <tr>
          <th>Curso</th>
          <th>Programa · Módulo</th>
          <th>Horario</th>
          <th>Aula</th>
          <th className="est-center">Estudiantes</th>
          <th className="est-center">Sin nota</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {data.courses.map(course => (
          <tr key={course.id} className={isCurrent(course) ? undefined : 'doc-row-muted'}>
            <td>
              <strong>{courseName(course)}</strong>
              {course.year && <span className="est-enroll-sub">{course.year}</span>}
            </td>
            <td className="est-muted">{course.career.name} · {course.module.name}</td>
            <td className="est-muted">{course.schedule || 'Por definir'}</td>
            <td className="est-muted">
              {course.classroom || 'Por asignar'}
              <span className="est-enroll-sub">{course.modality}</span>
            </td>
            <td className="est-center">{course.student_count}</td>
            <td className="est-center">{course.pending_count}</td>
            <td className="doc-actions">
              <Link className="est-btn est-btn--small doc-btn-ghost" to={`${BASE}/estudiantes?curso=${course.id}`}>
                Estudiantes
              </Link>
              <Link className="est-btn est-btn--small" to={`${BASE}/notas?curso=${course.id}`}>Notas</Link>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const MisEstudiantes: React.FC<SectionProps> = ({ data, courseId, onSelectCourse }) => {
  const { detail, error } = useCourseDetail(courseId);

  return (
    <div className="est-grades">
      <div className="est-grades-filters">
        <CourseFilter courses={data.courses} courseId={courseId} onSelect={onSelectCourse} />
      </div>
      {error && <Empty>{error}</Empty>}
      {!error && !detail && <Empty>Cargando estudiantes…</Empty>}
      {detail && !detail.students.length && <Empty>Este curso todavía no tiene estudiantes.</Empty>}
      {detail && detail.students.length > 0 && (
        <table className="est-table est-table--framed">
          <thead>
            <tr>
              <th className="est-center">#</th>
              <th>Código</th>
              <th>Estudiante</th>
              <th>Documento</th>
              <th>Correo</th>
              <th className="est-center">Def</th>
              <th className="est-center">Estado</th>
            </tr>
          </thead>
          <tbody>
            {detail.students.map((row, i) => (
              <tr key={row.grade_id}>
                <td className="est-center est-muted">{i + 1}</td>
                <td className="est-muted">{row.student.student_code ?? '—'}</td>
                <td>{row.student.full_name}</td>
                <td className="est-muted">{row.student.document_number}</td>
                <td className="est-muted">{row.student.email ?? '—'}</td>
                <td className="est-center">{formatNumber(row.grade)}</td>
                <td className="est-center">
                  <span className={`est-status est-status--${STATUS_CLASS[row.status]}`}>{STATUS_LABEL[row.status]}</span>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={7} className="est-grades-avg-label">{detail.students.length} estudiantes en el curso</td>
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );
};

type Message = { tone: 'error' | 'ok'; text: string } | null;

const Cortes: React.FC<{ detail: CourseDetail; onChange: (d: CourseDetail) => void; setMessage: (m: Message) => void }> = ({
  detail, onChange, setMessage,
}) => {
  const courseId = detail.course.id;
  const [name, setName] = useState('');
  const [weight, setWeight] = useState('');
  const [editing, setEditing] = useState<{ id: number; name: string; weight: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const remaining = Math.max(100 - detail.weights_total, 0);

  const run = async (action: () => Promise<CourseDetail>, ok: string) => {
    setBusy(true);
    setMessage(null);
    try {
      onChange(await action());
      setMessage({ tone: 'ok', text: ok });
      return true;
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof Error ? err.message : 'No se pudo guardar el corte.' });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    if (await run(() => addGradeComponent(courseId, name.trim(), Number(weight)), 'Corte agregado.')) {
      setName('');
      setWeight('');
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    const ok = await run(
      () => updateGradeComponent(courseId, editing.id, editing.name.trim(), Number(editing.weight)),
      'Corte actualizado.',
    );
    if (ok) setEditing(null);
  };

  const remove = (id: number, label: string) => {
    if (!window.confirm(`¿Eliminar el corte "${label}"? Se borrarán las notas registradas en él.`)) return;
    run(() => deleteGradeComponent(courseId, id), 'Corte eliminado.');
  };

  return (
    <div className="doc-cortes">
      <div className="doc-cortes-head">
        <strong>Cortes de evaluación</strong>
        <span className={detail.weights_total === 100 ? 'doc-total is-ok' : 'doc-total'}>
          Suman {formatNumber(detail.weights_total)} % de 100 %
        </span>
      </div>
      {!detail.components.length && (
        <p className="doc-cortes-hint">
          Sin cortes, registras directamente la nota definitiva. Si agregas cortes (p. ej. Primer corte 30 %),
          la definitiva se calcula con sus porcentajes.
        </p>
      )}
      {detail.components.length > 0 && (
        <ul className="doc-cortes-list">
          {detail.components.map(c => (
            <li key={c.id}>
              {editing?.id === c.id ? (
                <>
                  <input className="doc-input" value={editing.name} maxLength={60}
                    onChange={e => setEditing({ ...editing, name: e.target.value })} />
                  <input className="doc-input doc-input--small" type="number" min={1} max={100} step="0.5"
                    value={editing.weight} onChange={e => setEditing({ ...editing, weight: e.target.value })} />
                  <span>%</span>
                  <button type="button" className="est-btn est-btn--small" disabled={busy} onClick={saveEdit}>Guardar</button>
                  <button type="button" className="est-btn est-btn--small doc-btn-ghost" onClick={() => setEditing(null)}>
                    Cancelar
                  </button>
                </>
              ) : (
                <>
                  <span className="doc-corte-name">{c.name}</span>
                  <span className="doc-corte-weight">{formatNumber(c.weight)} %</span>
                  <button type="button" className="doc-link"
                    onClick={() => setEditing({ id: c.id, name: c.name, weight: String(c.weight) })}>
                    Editar
                  </button>
                  <button type="button" className="doc-link doc-link--danger" disabled={busy}
                    onClick={() => remove(c.id, c.name)}>
                    Eliminar
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {remaining > 0 && (
        <form className="doc-cortes-add" onSubmit={add}>
          <input className="doc-input" placeholder="Nombre del corte" value={name} maxLength={60} required
            onChange={e => setName(e.target.value)} />
          <input className="doc-input doc-input--small" type="number" placeholder="%" min={1} max={remaining} step="0.5"
            value={weight} required onChange={e => setWeight(e.target.value)} />
          <span>%</span>
          <button type="submit" className="est-btn est-btn--small" disabled={busy}>Agregar corte</button>
        </form>
      )}
    </div>
  );
};

function draftNumber(value: string | undefined): number | null | undefined {
  if (value === undefined) return undefined;
  if (value.trim() === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

const RegistrarNotas: React.FC<SectionProps> = ({ data, courseId, onSelectCourse, onCourseChanged }) => {
  const { detail, setDetail, error } = useCourseDetail(courseId);
  const [drafts, setDrafts] = useState<Record<number, Record<string, string>>>({});
  const [finals, setFinals] = useState<Record<number, string>>({});
  const [message, setMessage] = useState<Message>(null);
  const [saving, setSaving] = useState(false);
  const [closing, setClosing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    setDrafts({});
    setFinals({});
    setMessage(null);
    setConfirmOpen(false);
  }, [courseId]);

  const applyDetail = (next: CourseDetail) => {
    setDetail(next);
    onCourseChanged();
  };

  const components = useMemo(() => detail?.components ?? [], [detail]);
  const hasComponents = components.length > 0;

  const valueFor = (row: CourseStudentRow, cid: number) => {
    const draft = drafts[row.grade_id]?.[String(cid)];
    if (draft !== undefined) return draft;
    const saved = row.values[String(cid)];
    return saved === undefined ? '' : String(saved);
  };

  const changedRows = useMemo(() => {
    if (!detail) return [];
    return detail.students.flatMap(row => {
      const rowDrafts = drafts[row.grade_id];
      if (!rowDrafts) return [];
      const values: ComponentValues = {};
      Object.entries(rowDrafts).forEach(([cid, raw]) => {
        const parsed = draftNumber(raw);
        const saved = row.values[cid] ?? null;
        if (parsed !== undefined && parsed !== saved) values[cid] = parsed;
      });
      return Object.keys(values).length ? [{ grade_id: row.grade_id, values }] : [];
    });
  }, [detail, drafts]);

  const accumulated = (row: CourseStudentRow) => {
    let total = 0;
    components.forEach(c => {
      const n = draftNumber(valueFor(row, c.id));
      if (typeof n === 'number') total += (n * c.weight) / 100;
    });
    return Math.round(total * 100) / 100;
  };

  const save = async () => {
    if (!detail || !changedRows.length) return;
    setSaving(true);
    setMessage(null);
    try {
      applyDetail(await saveComponentGrades(detail.course.id, changedRows));
      setDrafts({});
      setMessage({ tone: 'ok', text: 'Notas guardadas.' });
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof Error ? err.message : 'No se pudieron guardar las notas.' });
    } finally {
      setSaving(false);
    }
  };

  const pendingRows = useMemo(() => detail?.students.filter(r => r.editable) ?? [], [detail]);

  const finalFor = (row: CourseStudentRow): number | null => {
    if (hasComponents) return row.complete ? row.partial : null;
    const n = draftNumber(finals[row.grade_id]);
    return typeof n === 'number' ? n : null;
  };

  const closeBlocker = (): string | null => {
    if (!detail) return null;
    if (hasComponents) {
      if (detail.weights_total !== 100) return 'Los cortes deben sumar 100 % antes de cerrar las notas.';
      if (changedRows.length) return 'Guarda primero los cambios de las notas.';
    }
    const missing = pendingRows.filter(r => finalFor(r) === null);
    if (!missing.length) return null;
    const names = missing.slice(0, 3).map(r => r.student.full_name).join(', ');
    const extra = missing.length > 3 ? ` y ${missing.length - 3} más` : '';
    return hasComponents
      ? `Faltan notas de cortes para: ${names}${extra}.`
      : `Falta la nota definitiva de: ${names}${extra}.`;
  };

  const readyToClose = pendingRows.length > 0 && closeBlocker() === null;

  const requestClose = () => {
    const blocker = closeBlocker();
    if (blocker) {
      setMessage({ tone: 'error', text: blocker });
      return;
    }
    setMessage(null);
    setConfirmOpen(true);
  };

  const closeCourse = async () => {
    if (!detail) return;
    setClosing(true);
    try {
      let values: Record<string, number> | undefined;
      if (!hasComponents) {
        values = {};
        pendingRows.forEach(r => { values![String(r.grade_id)] = finalFor(r) as number; });
      }
      applyDetail(await finalizeCourse(detail.course.id, values));
      setFinals({});
      setMessage({ tone: 'ok', text: `Notas enviadas: se cerraron las notas definitivas de ${pendingRows.length} estudiante(s).` });
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof Error ? err.message : 'No se pudieron cerrar las notas.' });
    } finally {
      setClosing(false);
      setConfirmOpen(false);
    }
  };

  const setDraft = (gradeId: number, cid: number, value: string) => {
    setDrafts(prev => ({ ...prev, [gradeId]: { ...prev[gradeId], [String(cid)]: value } }));
  };

  const scale = detail?.scale;
  const passes = (value: number) => scale ? value >= scale.passing_grade : false;

  return (
    <div className="est-grades">
      <div className="est-grades-filters">
        <CourseFilter courses={data.courses} courseId={courseId} onSelect={onSelectCourse} />
        {scale && (
          <span className="doc-scale">
            Escala {formatNumber(scale.min_grade)} – {formatNumber(scale.max_grade)} · Aprueba con {formatNumber(scale.passing_grade)}
          </span>
        )}
      </div>

      {error && <Empty>{error}</Empty>}
      {!error && !detail && <Empty>Cargando notas…</Empty>}

      {detail && (
        <>
          <Cortes detail={detail} onChange={applyDetail} setMessage={setMessage} />
          {message && <div className={`est-message est-message--${message.tone}`}>{message.text}</div>}

          {!detail.students.length ? (
            <Empty>Este curso todavía no tiene estudiantes.</Empty>
          ) : (
            <div className="doc-grades-scroll">
              <table className="est-table est-table--framed doc-grades">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Estudiante</th>
                    {components.map(c => (
                      <th key={c.id} className="est-center">{c.name}<span className="doc-th-sub">{formatNumber(c.weight)} %</span></th>
                    ))}
                    {hasComponents && <th className="est-center">Acumulado</th>}
                    <th className="est-center">Definitiva</th>
                    <th className="est-center">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.students.map(row => (
                    <tr key={row.grade_id}>
                      <td className="est-muted">{row.student.student_code ?? '—'}</td>
                      <td>{row.student.full_name}</td>
                      {components.map(c => (
                        <td key={c.id} className="est-center">
                          <input
                            className="doc-input doc-input--grade"
                            type="number"
                            inputMode="decimal"
                            min={scale?.min_grade}
                            max={scale?.max_grade}
                            step="0.1"
                            value={valueFor(row, c.id)}
                            disabled={!row.editable}
                            onChange={e => setDraft(row.grade_id, c.id, e.target.value)}
                            aria-label={`${c.name} de ${row.student.full_name}`}
                          />
                        </td>
                      ))}
                      {hasComponents && <td className="est-center est-muted">{formatNumber(accumulated(row))}</td>}
                      <td className="est-center">
                        {!hasComponents && row.editable ? (
                          <input
                            className="doc-input doc-input--grade"
                            type="number"
                            inputMode="decimal"
                            min={scale?.min_grade}
                            max={scale?.max_grade}
                            step="0.1"
                            value={finals[row.grade_id] ?? ''}
                            onChange={e => setFinals(prev => ({ ...prev, [row.grade_id]: e.target.value }))}
                            aria-label={`Nota definitiva de ${row.student.full_name}`}
                          />
                        ) : (
                          <strong>{formatNumber(row.grade)}</strong>
                        )}
                      </td>
                      <td className="est-center">
                        <span className={`est-status est-status--${STATUS_CLASS[row.status]}`}>{STATUS_LABEL[row.status]}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {pendingRows.length > 0 && (
            <div className="doc-save-bar">
              {hasComponents && (
                <>
                  <span className="est-muted est-small">
                    {changedRows.length ? `${changedRows.length} estudiante(s) con cambios sin guardar` : 'No hay cambios pendientes'}
                  </span>
                  <button type="button" className="est-btn" disabled={!changedRows.length || saving} onClick={save}>
                    {saving ? 'Guardando…' : 'Guardar notas'}
                  </button>
                </>
              )}
              {readyToClose && (
                <button type="button" className="est-btn doc-btn-danger" disabled={saving || closing} onClick={requestClose}>
                  Cerrar notas del curso
                </button>
              )}
            </div>
          )}

          {pendingRows.length > 0 && !readyToClose && (
            <p className="doc-close-hint">
              🔒 El botón para cerrar las notas del curso aparecerá cuando todas las notas estén completas y guardadas.
            </p>
          )}

          {detail.students.length > 0 && !pendingRows.length && (
            <div className="est-message est-message--ok">Las notas definitivas de este curso ya están cerradas.</div>
          )}

          {confirmOpen && (
            <div className="doc-modal-backdrop" role="presentation" onClick={() => !closing && setConfirmOpen(false)}>
              <div
                className="doc-modal"
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="doc-close-title"
                onClick={e => e.stopPropagation()}
              >
                <h3 id="doc-close-title">⚠️ ¿Enviar las notas definitivas?</h3>
                <p>
                  Estás a punto de enviar todas las notas de <strong>{courseName(detail.course)}</strong> y cerrar el
                  curso para <strong>{pendingRows.length} estudiante(s)</strong>. Las notas quedarán registradas como
                  definitivas y los estudiantes las verán en su portal.
                </p>
                <p className="doc-modal-warning">
                  Una vez enviadas <strong>no podrás modificarlas</strong>, ni los cortes ni la nota definitiva.
                  Revisa la lista antes de continuar.
                </p>
                <div className="doc-modal-list">
                  <table className="est-table">
                    <thead>
                      <tr>
                        <th>Estudiante</th>
                        <th className="est-center">Definitiva</th>
                        <th className="est-center">Resultado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingRows.map(row => {
                        const value = finalFor(row) as number;
                        const ok = passes(value);
                        return (
                          <tr key={row.grade_id}>
                            <td>{row.student.full_name}</td>
                            <td className="est-center"><strong>{formatNumber(value)}</strong></td>
                            <td className="est-center">
                              <span className={`est-status est-status--${ok ? STATUS_CLASS.PASSED : STATUS_CLASS.FAILED}`}>
                                {ok ? 'Aprueba' : 'Reprueba'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="doc-modal-actions">
                  <button type="button" className="est-btn doc-btn-ghost" disabled={closing} onClick={() => setConfirmOpen(false)}>
                    Cancelar
                  </button>
                  <button type="button" className="est-btn doc-btn-danger" disabled={closing} onClick={closeCourse}>
                    {closing ? 'Enviando…' : 'Sí, enviar y cerrar notas'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const Horario: React.FC<SectionProps> = ({ data }) => {
  const items = useMemo(() => data.courses.filter(isCurrent).map((course, i) => ({
    course,
    color: BLOCK_COLORS[i % BLOCK_COLORS.length],
    slots: scheduleSlots(course.schedule_slots, course.schedule),
  })), [data.courses]);
  const blocks = useMemo(() => items.map(({ course, color, slots }) => ({
    key: String(course.id),
    title: courseName(course),
    detail: course.classroom,
    color,
    slots,
  })), [items]);

  return (
    <div className="est-schedule">
      <div className="est-schedule-main">
        <div className="est-grades-bar est-schedule-bar">
          <span>Ver horario</span>
          <PrintButton />
        </div>
        <div className="est-schedule-info">
          <div className="est-schedule-student">
            <span>Docente: {data.teacher.full_name}</span>
            <span>Identificación: {data.teacher.document_number}</span>
          </div>
        </div>
        <WeekSchedule blocks={blocks} />
      </div>

      <aside className="est-schedule-side">
        <div className="est-grades-bar">Descripción del horario</div>
        {!items.length ? (
          <p className="est-schedule-empty">No tienes cursos activos en este momento.</p>
        ) : (
          <ul className="est-schedule-list">
            {items.map(({ course, color, slots }) => (
              <li key={course.id}>
                <span className="est-schedule-dot" style={{ background: color }} />
                <div>
                  <strong>{courseName(course)}</strong>
                  <span>{course.schedule || 'Horario por definir'}</span>
                  <span>
                    {course.classroom || 'Salón por asignar'} · {course.modality} · {course.student_count} estudiantes
                  </span>
                  {course.schedule && !slots.length && (
                    <span className="est-schedule-warn">No se pudo ubicar en la cuadrícula</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </aside>
    </div>
  );
};

const CambiarContrasena: React.FC<SectionProps> = ({ mustChangePassword, onPasswordChanged }) => (
  <ChangePasswordForm
    mustChangePassword={mustChangePassword}
    onPasswordChanged={onPasswordChanged}
    changePassword={changeTeacherPassword}
    forceChangePassword={forceChangeTeacherPassword}
  />
);

const SECTION_CONTENT: Record<SectionId, React.FC<SectionProps>> = {
  cursos: MisCursos,
  estudiantes: MisEstudiantes,
  notas: RegistrarNotas,
  horario: Horario,
  contrasena: CambiarContrasena,
};

const DocenteDashboard: React.FC = () => {
  const { seccion } = useParams<{ seccion?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const session = getTeacherSession();
  const [data, setData] = useState<TeacherPortalData | null>(null);
  const [error, setError] = useState('');
  const [mustChangePassword, setMustChangePassword] = useState(Boolean(session?.mustChangePassword));
  const [welcome, setWelcome] = useState('');

  useEffect(() => {
    if (seccion) setWelcome('');
  }, [seccion]);

  const handlePasswordChanged = () => {
    if (!mustChangePassword) return;
    setMustChangePassword(false);
    setWelcome('Tu contraseña se actualizó correctamente. ¡Bienvenido(a) al Portal Laboral!');
    navigate(BASE, { replace: true });
  };

  const logout = useCallback(() => {
    logoutTeacher();
    navigate('/portal-laboral', { replace: true });
  }, [navigate]);

  const loadData = useCallback(async (isCancelled: () => boolean = () => false) => {
    if (!getTeacherSession()) return;
    try {
      const result = await fetchTeacherPortal();
      if (!isCancelled()) setData(result);
    } catch (err) {
      if (isCancelled()) return;
      if (err instanceof SessionExpiredError) {
        navigate('/portal-laboral', { replace: true });
        return;
      }
      setError(err instanceof Error ? err.message : 'No se pudieron cargar tus datos.');
    }
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;
    loadData(() => cancelled);
    return () => { cancelled = true; };
  }, [loadData]);

  const courses = useMemo(() => data?.courses ?? [], [data]);
  const requested = Number(searchParams.get('curso'));
  const courseId = courses.find(c => c.id === requested)?.id
    ?? courses.find(c => c.pending_count > 0)?.id
    ?? courses[0]?.id
    ?? null;

  if (!session) {
    return <Navigate to="/portal-laboral" replace />;
  }

  const active = SECTIONS.find(s => s.id === seccion);
  if (seccion && !active) {
    return <Navigate to={BASE} replace />;
  }
  if (mustChangePassword && seccion !== 'contrasena') {
    return <Navigate to={`${BASE}/contrasena`} replace />;
  }

  const Content = active ? SECTION_CONTENT[active.id] : null;

  return (
    <>
      <PortalHeader
        homeTo={BASE}
        name={data?.teacher.full_name ?? null}
        photo={data?.teacher.photo}
        meta={[
          `Usuario: ${data?.teacher.username ?? '—'}`,
          `Documento: ${data?.teacher.document_number ?? '—'}`,
        ]}
        ready={Boolean(data)}
        onUploadPhoto={uploadTeacherPhoto}
        onPhotoChanged={photo => setData(prev => (prev ? { ...prev, teacher: { ...prev.teacher, photo } } : prev))}
        onLogout={logout}
      />

      <div className="est-page">
        <div className="est-shell">
          <div className="est-titlebar">
            <span>Portal Laboral</span>
          </div>

          {error && <div className="est-section"><Empty>{error}</Empty></div>}

          {!error && !data && <div className="est-section"><Empty>Cargando tu información…</Empty></div>}

          {data && !active && welcome && (
            <div className="est-section">
              <div className="est-message est-message--ok">{welcome}</div>
            </div>
          )}

          {data && !active && (
            <nav className="est-menu" aria-label="Opciones del portal">
              {SECTIONS.map(({ id, label, icon: Icon }) => (
                <Link key={id} to={`${BASE}/${id}`} className="est-tile">
                  <span className="est-tile-icon"><Icon /></span>
                  <span className="est-tile-label">{label}</span>
                </Link>
              ))}
            </nav>
          )}

          {data && active && Content && (
            <section className="est-section">
              {!mustChangePassword && (
                <Link to={BASE} className="est-back">← Volver al menú</Link>
              )}
              <div className="est-section-head">
                <span className="est-section-icon"><active.icon /></span>
                <h1 className="est-section-title">{active.label}</h1>
              </div>
              {active.perCourse && !courses.length ? (
                <Empty>Aún no tienes cursos asignados.</Empty>
              ) : (
                <Content
                  data={data}
                  courseId={courseId}
                  onSelectCourse={id => setSearchParams({ curso: String(id) })}
                  onCourseChanged={() => loadData()}
                  mustChangePassword={mustChangePassword}
                  onPasswordChanged={handlePasswordChanged}
                />
              )}
            </section>
          )}
        </div>
      </div>
    </>
  );
};

export default DocenteDashboard;
